'use client';
import {
  useMutation,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { fetchApi, ApiClientError } from '@/lib/api/client';
import { adminKeys } from '@/hooks/api/query-keys';
import {
  GenerateResponseSchema,
  ExchangeResponseSchema,
  SessionsListSchema,
  type SessionsList,
} from '@/lib/admin/zod/impersonation';

// ─── Admin auth helpers (self-contained, mirrors use-reset-peer-2fa.ts) ────────

const ADMIN_TOKEN_KEY = 'hf-admin:access-token';

function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function getAdminHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function handleAdmin401(error: unknown): never {
  if (
    error instanceof ApiClientError &&
    error.statusCode === 401 &&
    typeof window !== 'undefined'
  ) {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    document.cookie = 'hf-admin:session=; path=/; max-age=0';
    window.location.href = '/admin/login';
  }
  throw error;
}

async function adminFetch<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  if (typeof window === 'undefined') return undefined as unknown as T;
  try {
    return await fetchApi<T>(endpoint, {
      ...options,
      skipAuth: true,
      headers: {
        ...getAdminHeaders(),
        ...(options?.headers as Record<string, string>),
      },
    });
  } catch (error) {
    handleAdmin401(error);
  }
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Generate an opaque exchange token for impersonating a target user.
 * Requires super-admin JWT (sent via admin Authorization header).
 * Returns { token, expiresAt, redirectUrl }.
 */
export function useGenerateImpersonation() {
  return useMutation({
    mutationFn: async (params: {
      userId: string;
      companyId: string;
      workspaceId: string;
      reason: string;
    }) => {
      const raw = await adminFetch<unknown>('/admin/impersonation/generate', {
        method: 'POST',
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' },
      });
      return GenerateResponseSchema.parse(raw);
    },
  });
}

/**
 * Exchange an opaque token for real user session credentials.
 * PUBLIC endpoint — the token in the body authenticates the request.
 * Called from the exchange page (new browser tab) with no existing session.
 */
export function useExchangeImpersonation() {
  return useMutation({
    mutationFn: async (token: string) => {
      const raw = await fetchApi<unknown>('/admin/impersonation/exchange', {
        method: 'POST',
        body: JSON.stringify({ token }),
        skipAuth: true,
        headers: { 'Content-Type': 'application/json' },
      });
      return ExchangeResponseSchema.parse(raw);
    },
  });
}

/**
 * End the current impersonation session.
 * Called from inside the impersonated session using the USER access token.
 * The backend reads impersonatedBy from the JWT — no extra body needed.
 */
export function useEndImpersonation() {
  return useMutation({
    mutationFn: async () => {
      await fetchApi<unknown>('/admin/impersonation/end', {
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
      });
    },
  });
}

/**
 * List impersonation sessions (super-admin audit log).
 * Requires super-admin JWT.
 * Supports paginated filtering via query params.
 */
export function useImpersonationSessions(
  filters: Record<string, unknown> = {},
) {
  return useQuery({
    queryKey: adminKeys.impersonation.sessions(filters),
    queryFn: async (): Promise<SessionsList> => {
      const qs = new URLSearchParams(
        Object.entries(filters).reduce<Record<string, string>>((acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
          return acc;
        }, {}),
      ).toString();
      const raw = await adminFetch<unknown>(
        `/admin/impersonation/sessions${qs ? `?${qs}` : ''}`,
      );
      return SessionsListSchema.parse(raw);
    },
    placeholderData: keepPreviousData,
  });
}
